import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV_LINKS, openWebinar } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500",
        scrolled ? "glass border-b border-white/10" : "border-b border-transparent",
      )}
    >
      <nav className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => go("start")}
          className="flex items-center gap-2"
          aria-label="Kurier Connect – nach oben"
        >
          <img src="/brand/logo.png" alt="Kurier Connect" className="h-9 w-9 rounded-md object-contain sm:h-10 sm:w-10" />
          <span className="font-heading text-sm font-extrabold uppercase tracking-[0.14em] text-white sm:text-base">
            Kurier<span className="text-brand-green">Connect</span>
          </span>
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            type="button"
            onClick={openWebinar}
            data-testid="nav-cta"
            className="rounded-full bg-brand-green px-5 py-2 font-heading text-sm font-bold uppercase tracking-wide text-brand-bg glow-green-sm transition-transform hover:scale-[1.04]"
          >
            Kostenlos anmelden
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full glass text-white md:hidden"
          aria-label="Menü"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-brand-bg/97 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left font-heading text-lg font-semibold text-white/85 transition-colors hover:bg-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setOpen(false); openWebinar(); }}
                data-testid="nav-mobile-cta"
                className="mt-2 rounded-xl bg-brand-green py-3.5 text-center font-heading text-base font-bold uppercase tracking-wide text-brand-bg glow-green-sm"
              >
                Kostenlos anmelden
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
